import { useId, type ReactNode } from 'react';

import { X } from 'lucide-react';

import { cx } from '@/lib/cx';
import { useOverlay } from '@/lib/useOverlay';

import styles from './Drawer.module.css';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  /** Rendered before the heading: an avatar on a record drawer, for instance. */
  lead?: ReactNode;
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

/**
 * A side panel for editing a record.
 *
 * A drawer rather than a modal wherever the list behind it is worth keeping in view,
 * which is most of this application.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  lead,
  width = 480,
  children,
  footer,
  className,
}: DrawerProps) {
  const titleId = useId();
  const ref = useOverlay(open, onClose);

  if (!open) return null;

  return (
    <>
      <div className={styles.scrim} onMouseDown={onClose} />
      <aside
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ inlineSize: width }}
        className={cx(styles.drawer, className)}
      >
        <header className={styles.head}>
          {lead}
          <div className={styles.heading}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {/* Only the body scrolls, so the heading and the actions stay reachable
            however long the form gets. */}
        <div className={styles.body}>{children}</div>

        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </aside>
    </>
  );
}
